import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BrandingContext = createContext(null);
export const useBranding = () => useContext(BrandingContext);

export function applyBranding(b) {
  if (!b) return;
  const r = document.documentElement;
  if (b.primary) r.style.setProperty("--primary", b.primary);
  if (b.accent) r.style.setProperty("--brand", b.accent);
  if (b.background) r.style.setProperty("--background", b.background);
  if (b.foreground) r.style.setProperty("--foreground", b.foreground);
  if (b.radius != null) r.style.setProperty("--radius", b.radius);
  if (b.heading_font) r.style.setProperty("--font-heading", `'${b.heading_font}', system-ui, sans-serif`);
  if (b.body_font) r.style.setProperty("--font-body", `'${b.body_font}', system-ui, sans-serif`);
}

function ensureFont(name) {
  if (!name) return;
  const id = `gf-${name.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/branding");
      setBranding(data);
      applyBranding(data);
      ensureFont(data.heading_font);
      ensureFont(data.body_font);
      // Drive the browser tab title from Branding (e.g. "Barnard | Commission Calculator").
      document.title = data.app_name
        ? `${data.app_name} | ${data.tagline || "Commission Calculator"}`
        : "Commission Calculator";
      // Use the Branding logo as the browser tab icon (favicon).
      const favHref = data.logo_path ? `${API}/files/${data.logo_path}` : "/favicon.ico";
      document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove());
      const link = document.createElement("link");
      link.rel = "icon";
      link.href = favHref;
      document.head.appendChild(link);
    } catch (_) {}
  }, []);

  // Branding is public — load on mount and whenever the auth user changes.
  useEffect(() => { load(); }, [user, load]);

  const logoUrl = branding?.logo_path ? `${API}/files/${branding.logo_path}` : null;

  return (
    <BrandingContext.Provider value={{ branding, setBranding, reloadBranding: load, logoUrl }}>
      {children}
    </BrandingContext.Provider>
  );
}
